import React, { useState, useEffect } from 'react';
import { Calendar, Newspaper, Bell, Folder, Briefcase, BookOpen, Plus, Clock, Users } from 'lucide-react';
import { adminApi } from '../helpers/adminApi';

const AdminDashboard = () => {
  const [contentStats, setContentStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const contentTypes = [
    {
      key: 'events',
      label: 'Évènements',
      icon: Calendar,
      endpoint: '/api/events',
      color: 'primary',
      route: '/admin/events'
    },
    {
      key: 'patrimoine-actualites',
      label: 'Patrimoine & Actualités',
      icon: Newspaper,
      endpoint: '/api/patrimoine-actualites',
      color: 'info',
      route: '/admin/patrimoine-actualites'
    },
    {
      key: 'popups',
      label: 'Popups',
      icon: Bell,
      endpoint: '/api/popups',
      color: 'warning',
      route: '/admin/popups'
    },
    {
      key: 'collections',
      label: 'Collections',
      icon: Folder,
      endpoint: '/api/collections',
      color: 'success',
      route: '/admin/collections'
    },
    {
      key: 'hiring-ads',
      label: 'Vie administrative',
      icon: Briefcase,
      endpoint: '/api/hiring-ads',
      color: 'secondary',
      route: '/admin/hiring-ads'
    },
    {
      key: 'magazines',
      label: 'Magazines',
      icon: BookOpen,
      endpoint: '/api/magazines',
      color: 'danger',
      route: '/admin/magazines'
    }
  ];

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load content counts
      const stats = {};
      const activityItems = [];

      for (const contentType of contentTypes) {
        try {
          const data = await adminApi.fetchEntries(contentType.endpoint, {
            'sort[0]': 'updatedAt:desc',
            'populate': '*'
          });
          
          const entries = data.data || [];
          stats[contentType.key] = {
            total: entries.length,
            published: entries.filter(entry => entry.isPublished !== false).length,
            recent: entries.slice(0, 3)
          };

          // Add recent items to activity feed
          entries.slice(0, 2).forEach(entry => {
            activityItems.push({
              type: contentType.key,
              label: contentType.label,
              icon: contentType.icon,
              title: entry.title,
              updatedAt: entry.updatedAt,
              id: entry.documentId || entry.id
            });
          });
        } catch (error) {
          console.error(`Error loading ${contentType.key}:`, error);
          stats[contentType.key] = { total: 0, published: 0, recent: [] };
        }
      }

      // Sort activity by date
      activityItems.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      
      setContentStats(stats);
      setRecentActivity(activityItems.slice(0, 5));
    } catch (error) {
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleQuickCreate = (route) => {
    window.location.href = route;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Tableau de bord</h2>
          <p className="text-muted mb-0">Vue d'ensemble de votre contenu</p>
        </div>
      </div>

      {/* Content Overview Cards */}
      <div className="row mb-4">
        {contentTypes.map((contentType) => {
          const stats = contentStats[contentType.key] || { total: 0, published: 0 };
          const IconComponent = contentType.icon;
          
          return (
            <div key={contentType.key} className="col-md-6 col-lg-4 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <div className={`p-2 rounded bg-${contentType.color} bg-opacity-10 me-3`}>
                      <IconComponent className={`text-${contentType.color}`} size={24} />
                    </div>
                    <div>
                      <h6 className="card-title mb-0">{contentType.label}</h6>
                      <small className="text-muted">Total: {stats.total}</small>
                    </div>
                  </div>
                  
                  {stats.total > 0 && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between text-sm">
                        <span>Publiés</span>
                        <span>{stats.published}</span>
                      </div>
                      <div className="progress" style={{ height: '4px' }}>
                        <div 
                          className={`progress-bar bg-${contentType.color}`} 
                          style={{ width: `${stats.total > 0 ? (stats.published / stats.total) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  <div className="d-grid">
                    <button
                      className={`btn btn-outline-${contentType.color} btn-sm`}
                      onClick={() => handleQuickCreate(contentType.route)}
                    >
                      <Plus size={16} className="me-1" />
                      Ajouter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="row">
        {/* Quick Actions */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Actions rapides</h5>
            </div>
            <div className="card-body">
              <div className="row g-2">
                {contentTypes.slice(0, 4).map((contentType) => {
                  const IconComponent = contentType.icon;
                  return (
                    <div key={contentType.key} className="col-6">
                      <button
                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center p-3"
                        onClick={() => handleQuickCreate(contentType.route)}
                      >
                        <IconComponent size={20} className="me-2" />
                        <div className="text-start">
                          <div className="fw-bold">Ajouter</div>
                          <small>{contentType.label}</small>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-3 pt-3 border-top">
                <div className="row g-2">
                  <div className="col-6">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={() => handleQuickCreate('/admin/settings')}
                    >
                      Paramètres
                    </button>
                  </div>
                  <div className="col-6">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={() => handleQuickCreate('/admin/users')}
                    >
                      <Users size={16} className="me-1" />
                      Utilisateurs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity Timeline */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Activité récente</h5>
            </div>
            <div className="card-body">
              {recentActivity.length === 0 ? (
                <div className="text-center text-muted py-3">
                  <Clock size={32} className="mb-2 opacity-50" />
                  <p className="mb-0">Aucune activité récente</p>
                </div>
              ) : (
                <div className="timeline">
                  {recentActivity.map((activity, index) => {
                    const IconComponent = activity.icon;
                    return (
                      <div key={index} className="timeline-item d-flex mb-3">
                        <div className="flex-shrink-0 me-3">
                          <div className="p-2 rounded-circle bg-light">
                            <IconComponent size={16} className="text-primary" />
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1 fs-6">{activity.title}</h6>
                              <small className="text-muted">{activity.label}</small>
                            </div>
                            <small className="text-muted">{formatDate(activity.updatedAt)}</small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {recentActivity.length > 0 && (
                <div className="text-center pt-2 border-top">
                  <small className="text-muted">Dernières modifications</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Events Section */}
      {/* {contentStats.events && contentStats.events.total > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Événements à venir</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {contentStats.events.recent
                    .filter(event => event.startDate && new Date(event.startDate) > new Date())
                    .slice(0, 3)
                    .map((event, index) => (
                    <div key={index} className="col-md-4 mb-3">
                      <div className="border rounded p-3">
                        <h6 className="mb-2">{event.title}</h6>
                        <div className="d-flex align-items-center text-muted">
                          <Calendar size={16} className="me-2" />
                          <small>{formatDate(event.startDate)}</small>
                        </div>
                        {event.isPublished && (
                          <span className="badge bg-success mt-2">Publié</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {contentStats.events.recent.filter(event => event.startDate && new Date(event.startDate) > new Date()).length === 0 && (
                  <div className="text-center text-muted py-3">
                    <Calendar size={32} className="mb-2 opacity-50" />
                    <p className="mb-0">Aucun événement à venir</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default AdminDashboard;